import React, { useMemo } from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { makeStyles } from 'tss-react/mui';
import { isEmpty } from '@utils/validationUtils';
import { cloneDeep } from 'lodash';
import { IAmendmentGeneralData } from '../line-amendment';
import { IAmendmentCover, IAmendmentPageState } from '..';
import SelectFormField from '@components/form-fields/SelectFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import { validateCoverRepeaterField } from './validation';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { removeObjectAtIndex } from '@utils/helper-utils';
import { getAmendmentInitialState } from '../content';
import { useLazyQuery } from '@apollo/client';
import { getNonSelectedPlanCoversQuery } from '../queries';
import { IAbstractRecord } from '@models';
import { convertToCoversLists } from '../utils';
import DateService from '@services/DateService';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import { MAIN_THEME } from '@styles';

interface IPolicyCovers {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onChange: (pageState: IAmendmentPageState) => void;
  isDisabled: boolean;
  isGroupPolicy?: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 23.5%)`,
    gap: '0 2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  switchLabelWrapper: {
    width: 'fit-content',
    cursor: 'pointer',
  },
}));

const PolicyCovers: React.FC<IPolicyCovers> = ({
  generalData,
  pageState,
  onChange,
  isDisabled,
  isGroupPolicy,
}) => {
  const { classes } = useStyles();
  const values = pageState?.values?.covers;
  const errors = pageState.errors.covers;
  const touched = pageState.touched.covers;

  const initialState = useMemo(
    () => getAmendmentInitialState(generalData.line.category),
    [generalData.line.category]
  );

  const [getNonSelectedCovers, { data: coversData, loading, called }] =
    useLazyQuery(getNonSelectedPlanCoversQuery, {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

  if (generalData.plan.id && !called) {
    getNonSelectedCovers({
      variables: {
        selectedPlanID: generalData.plan.id,
        selectedPlanCoverIDs: [],
      },
    });
  }

  const { allCoverslov, allCoversData } = useMemo((): {
    allCoverslov: Record<string, string>;
    allCoversData: Record<string, IAmendmentCover>;
  } => {
    if (!coversData)
      return {
        allCoverslov: {},
        allCoversData: {},
      };

    const { lov, data } = convertToCoversLists(
      coversData,
      generalData.currency.Code,
      generalData.proposal.planToPolicyRate,
      pageState.values.amendmentDetails.amendmentEffectiveDate,
      pageState.values.amendmentPolicyDetails.amendmentExpiryDate
    );

    const covers: Record<string, string> = {};
    coversData.PlanConfigManagement.queries.getNonSelectedPlanCover.forEach(
      (planCover: IAbstractRecord) => {
        const id = planCover.planConfigManagement_PlanCover_Id;
        const policyCover =
          planCover.planConfigManagement_PlanCover_PolicyCoverID.views
            .PlanConfigManagement_all.properties;

        covers[id] = `${policyCover.ExternalCode} - ${policyCover.Name}`;
      }
    );

    return {
      allCoverslov: lov,
      allCoversData: data,
    };
  }, [coversData, generalData, pageState.values.amendmentDetails]);

  const calculateProRataRatio = (
    values: IAmendmentPageState['values'],
    currentRowIndex: number
  ) => {
    const currentRowValues = values.covers[currentRowIndex];
    const coverDays = DateService.diff(
      currentRowValues.coverEffectiveTo,
      currentRowValues.coverEffectiveFrom,
      'day'
    );
    const policyDays = DateService.diff(
      values.amendmentPolicyDetails.amendmentExpiryDate,
      values.amendmentPolicyDetails.policyEffectiveDate,
      'day'
    );
    return coverDays / policyDays;
  };

  const onFieldBlur = (fieldName: keyof IAmendmentCover, rowIndex: number) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.covers[rowIndex][fieldName] = true;
    onChange(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IAmendmentCover,
    value: unknown,
    rowIndex: number,
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);
    const currentRowValues = newPageState.values.covers[rowIndex];
    const currentRowErrors = newPageState.errors.covers[rowIndex];
    const currentRowTouched = newPageState.touched.covers[rowIndex];

    if (fieldName === 'policyCover') {
      const isNew = pageState.values.covers[rowIndex].isNew;
      const newValues = allCoversData[value as string];

      if (isNew) {
        newValues.isNew = isNew;
        newValues.coverEffectiveFrom =
          pageState.values.amendmentDetails.amendmentEffectiveDate;
        newValues.amendmentCoverPremiumValue = 0;
        newValues.amendmentCoverPremiumPercentage = 0;
        newValues.amendmentCoverSumInsured = 0;
        newValues.amendmentExcessOnClaimAmount = 0;
        newValues.amendmentExcessOnClaimDays = 0;
        newValues.amendmentExcessOnClaimPercentage = 0;
      }

      newPageState.values.covers[rowIndex] = newValues;
      newPageState.errors.covers[rowIndex] = initialState.errors.covers[0];
      newPageState.touched.covers[rowIndex] = initialState.touched.covers[0];
    } else if (fieldName === 'isRemoved') {
      if (value === true) {
        // Backup the values only if the row is not already removed on the backend
        if (currentRowValues.isRemovedBackup === false) {
          currentRowValues.coverValuesBackUp = cloneDeep(currentRowValues);
        }

        const proRataRatio = calculateProRataRatio(
          newPageState.values,
          rowIndex
        );

        currentRowValues.coverEffectiveFrom =
          pageState.values.amendmentDetails.amendmentEffectiveDate;
        currentRowValues.amendmentCoverPremiumValue = -(
          currentRowValues.coverPremiumValue * proRataRatio
        );
        currentRowValues.amendmentCoverSumInsured =
          -currentRowValues.coverSumInsured;
        currentRowValues.amendmentExcessOnClaimAmount =
          -currentRowValues.excessOnClaimAmount;
      } else if (value === false) {
        if (currentRowValues.coverValuesBackUp) {
          const oldValues = currentRowValues.coverValuesBackUp;

          currentRowValues.coverEffectiveFrom = oldValues.coverEffectiveFrom;
          currentRowValues.proRataRatio = oldValues.proRataRatio;
          currentRowValues.amendmentCoverPremiumValue =
            oldValues.amendmentCoverPremiumValue;
          currentRowValues.amendmentCoverSumInsured =
            oldValues.amendmentCoverSumInsured;
          currentRowValues.amendmentExcessOnClaimAmount =
            oldValues.amendmentExcessOnClaimAmount;

          delete currentRowValues.coverValuesBackUp;
        } else {
          currentRowValues.coverEffectiveFrom =
            pageState.values.amendmentDetails.amendmentEffectiveDate;
          currentRowValues.proRataRatio = 0;
          currentRowValues.amendmentCoverPremiumValue = 0;
          currentRowValues.amendmentCoverSumInsured = 0;
          currentRowValues.amendmentExcessOnClaimAmount = 0;
        }
      }
    } else if (fieldName === 'coverPremiumType') {
      currentRowValues.coverPremiumPercentage = null;
      currentRowValues.coverPremiumValue = null;
      currentRowValues.amendmentCoverPremiumPercentage = null;
      currentRowValues.amendmentCoverPremiumValue = null;

      currentRowErrors.coverPremiumPercentage = '';
      currentRowErrors.coverPremiumValue = '';
      currentRowErrors.amendmentCoverPremiumPercentage = '';
      currentRowErrors.amendmentCoverPremiumValue = '';

      currentRowTouched.coverPremiumPercentage = false;
      currentRowTouched.coverPremiumValue = false;
      currentRowTouched.amendmentCoverPremiumPercentage = false;
      currentRowTouched.amendmentCoverPremiumValue = false;
    } else if (fieldName === 'excessOnClaimType') {
      currentRowValues.excessOnClaimPercentage = null;
      currentRowValues.excessOnClaimAmount = null;
      currentRowValues.excessOnClaimDays = null;
      currentRowValues.amendmentExcessOnClaimPercentage = null;
      currentRowValues.amendmentExcessOnClaimAmount = null;
      currentRowValues.amendmentExcessOnClaimDays = null;

      currentRowErrors.excessOnClaimPercentage = '';
      currentRowErrors.excessOnClaimAmount = '';
      currentRowErrors.excessOnClaimDays = '';
      currentRowErrors.amendmentExcessOnClaimPercentage = '';
      currentRowErrors.amendmentExcessOnClaimAmount = '';
      currentRowErrors.amendmentExcessOnClaimDays = '';

      currentRowTouched.excessOnClaimPercentage = false;
      currentRowTouched.excessOnClaimAmount = false;
      currentRowTouched.excessOnClaimDays = false;
      currentRowTouched.amendmentExcessOnClaimPercentage = false;
      currentRowTouched.amendmentExcessOnClaimAmount = false;
      currentRowTouched.amendmentExcessOnClaimDays = false;
    }

    (currentRowValues as any)[fieldName] = value;
    currentRowErrors[fieldName] = validateCoverRepeaterField(
      fieldName,
      value,
      currentRowValues
    );
    if (touched) {
      currentRowTouched[fieldName] = true;
    }
    onChange(newPageState);
  };

  const onRowAdd = () => {
    const newPageState = cloneDeep(pageState);
    newPageState.values.covers.push({
      isMain: false,
      isMandatory: null,
      policyCover: '',
      coverPremiumType: '',
      coverPremiumPercentage: null,
      coverPremiumValue: null,
      currency: '',
      isEditable: true,
      coverSumInsured: null,
      sumInsuredIsAdditive: null,
      excessOnClaimType: '',
      excessOnClaimAmount: null,
      excessOnClaimPercentage: null,
      excessOnClaimDays: null,
      clauseID: '',
      coverEffectiveFrom: '',
      coverEffectiveTo: '',
      amendmentCoverPremiumPercentage: 0,
      amendmentCoverPremiumValue: 0,
      amendmentCoverSumInsured: 0,
      amendmentExcessOnClaimAmount: 0,
      amendmentExcessOnClaimDays: 0,
      amendmentExcessOnClaimPercentage: 0,

      isNew: true,
      isRemoved: false,
      isRemovedBackup: null,
      proRataRatio: 0,
    });

    newPageState.errors.covers.push(initialState.errors.covers[0]);
    newPageState.touched.covers.push(initialState.touched.covers[0]);

    onChange(newPageState);
  };

  const onRowDelete = (rowIndex: number) => {
    const newPageState = cloneDeep(pageState);
    newPageState.values.covers = removeObjectAtIndex(
      newPageState.values.covers,
      rowIndex
    );
    newPageState.errors.covers = removeObjectAtIndex(
      newPageState.errors.covers,
      rowIndex
    );
    newPageState.touched.covers = removeObjectAtIndex(
      newPageState.touched.covers,
      rowIndex
    );
    onChange(newPageState);
  };

  const renderRow = (row: IAmendmentCover, rowIndex: number) => {
    const isNew = values[rowIndex].isNew;
    const disableVarianceField =
      !generalData.loggedInUser.allowPlanCoverEdits ||
      !row.isEditable ||
      !['NEW', 'IN_PROGRESS'].includes(generalData.amendment.status);

    const disableOriginalField = disableVarianceField || !isNew;

    const disableIsRemoved =
      !generalData.loggedInUser.allowPlanCoverEdits ||
      row.isMain ||
      row.isMandatory ||
      !['NEW', 'IN_PROGRESS'].includes(generalData.amendment.status);

    const filteredCovers = cloneDeep(allCoverslov);
    values.forEach((r) => {
      if (r !== row && r.policyCover) {
        delete filteredCovers[r.policyCover];
      }
    });

    return (
      <div className={classes.container} key={rowIndex}>
        {isNew && (
          <div className={classes.clearBtn}>
            <button
              className={classes.removeBtn}
              onClick={() => onRowDelete(rowIndex)}
            >
              <ClearIcon fontSize="small" />
            </button>
          </div>
        )}
        <div className={classes.fieldRow}>
          <SelectFormField
            key="policyCover"
            name="policyCover"
            title="Policy Cover"
            placeholder="Policy Cover"
            value={values[rowIndex].policyCover}
            error={
              touched[rowIndex].policyCover ? errors[rowIndex].policyCover : ''
            }
            onChange={(v) => onFieldUpdate('policyCover', v, rowIndex)}
            loader={loading}
            selectOptions={filteredCovers}
            disabled={disableOriginalField}
            material
          />
          {values[rowIndex].policyCover && (
            <>
              <CurrencyFormField
                key="coverSumInsured"
                name="coverSumInsured"
                title="Sum Insured"
                placeholder="Sum Insured"
                value={values[rowIndex].coverSumInsured || ''}
                error={
                  touched[rowIndex].coverSumInsured
                    ? errors[rowIndex].coverSumInsured
                    : ''
                }
                onBlur={() => onFieldBlur('coverSumInsured', rowIndex)}
                onChange={(v) =>
                  onFieldUpdate('coverSumInsured', v.target.value, rowIndex)
                }
                disabled={disableOriginalField}
                material
                currencySymbol={generalData.currency.Symbol}
              />
              <SelectFormField
                key="sumInsuredIsAdditive"
                name="sumInsuredIsAdditive"
                title="Additive SumInsured"
                placeholder="Additive SumInsured"
                value={
                  isEmpty(values[rowIndex].sumInsuredIsAdditive)
                    ? ''
                    : values[rowIndex].sumInsuredIsAdditive
                      ? 'YES'
                      : 'NO'
                }
                error={
                  touched[rowIndex].sumInsuredIsAdditive
                    ? errors[rowIndex].sumInsuredIsAdditive
                    : ''
                }
                onChange={(v) =>
                  onFieldUpdate(
                    'sumInsuredIsAdditive',
                    v === 'YES' ? true : false,
                    rowIndex
                  )
                }
                disabled={disableOriginalField}
                selectOptions={{ YES: 'Yes', NO: 'No' }}
                material
              />
              <SelectFormField
                key="isMandatory"
                name="isMandatory"
                title="Mandatory"
                placeholder="Mandatory"
                value={
                  isEmpty(values[rowIndex].isMandatory)
                    ? ''
                    : values[rowIndex].isMandatory
                      ? 'YES'
                      : 'NO'
                }
                error={
                  touched[rowIndex].isMandatory
                    ? errors[rowIndex].isMandatory
                    : ''
                }
                onChange={(v) =>
                  onFieldUpdate(
                    'isMandatory',
                    v === 'YES' ? true : false,
                    rowIndex
                  )
                }
                disabled={disableOriginalField}
                selectOptions={{ YES: 'Yes', NO: 'No' }}
                material
              />

              {!isGroupPolicy && (
                <>
                  <SelectFormField
                    key="coverPremiumType"
                    name="coverPremiumType"
                    title="Premium Type"
                    placeholder="Premium Type"
                    value={values[rowIndex].coverPremiumType}
                    error={
                      touched[rowIndex].coverPremiumType
                        ? errors[rowIndex].coverPremiumType
                        : ''
                    }
                    onChange={(v) =>
                      onFieldUpdate('coverPremiumType', v, rowIndex)
                    }
                    selectOptions={{ PERCENTAGE: 'Percentage', VALUE: 'Value' }}
                    disabled={disableOriginalField}
                    material
                  />
                  {values[rowIndex].coverPremiumType === 'PERCENTAGE' && (
                    <PercentageFormField
                      key="coverPremiumPercentage"
                      name="coverPremiumPercentage"
                      title="Premium Percentage"
                      placeholder="Premium Percentage"
                      value={values[rowIndex].coverPremiumPercentage}
                      error={
                        touched[rowIndex].coverPremiumPercentage
                          ? errors[rowIndex].coverPremiumPercentage
                          : ''
                      }
                      onBlur={() =>
                        onFieldBlur('coverPremiumPercentage', rowIndex)
                      }
                      onChange={(v) =>
                        onFieldUpdate(
                          'coverPremiumPercentage',
                          v.target.value,
                          rowIndex
                        )
                      }
                      disabled={disableOriginalField}
                      material
                    />
                  )}
                  {values[rowIndex].coverPremiumType === 'VALUE' && (
                    <CurrencyFormField
                      key="coverPremiumValue"
                      name="coverPremiumValue"
                      title="Premium"
                      placeholder="Premium"
                      value={values[rowIndex].coverPremiumValue || ''}
                      error={
                        touched[rowIndex].coverPremiumValue
                          ? errors[rowIndex].coverPremiumValue
                          : ''
                      }
                      onBlur={() => onFieldBlur('coverPremiumValue', rowIndex)}
                      onChange={(v) =>
                        onFieldUpdate(
                          'coverPremiumValue',
                          v.target.value,
                          rowIndex
                        )
                      }
                      disabled={disableOriginalField}
                      material
                      currencySymbol={generalData.currency.Symbol}
                    />
                  )}
                </>
              )}

              <SelectFormField
                key="excessOnClaimType"
                name="excessOnClaimType"
                title="Excess Type"
                placeholder="Excess Type"
                value={values[rowIndex].excessOnClaimType}
                error={
                  touched[rowIndex].excessOnClaimType
                    ? errors[rowIndex].excessOnClaimType
                    : ''
                }
                onChange={(v) =>
                  onFieldUpdate('excessOnClaimType', v, rowIndex)
                }
                selectOptions={{
                  PERCENTAGE: 'Percentage',
                  VALUE: 'Value',
                  DAYS: 'Days',
                }}
                disabled={disableOriginalField}
                material
              />
              {values[rowIndex].excessOnClaimType === 'PERCENTAGE' && (
                <PercentageFormField
                  key="excessOnClaimPercentage"
                  name="excessOnClaimPercentage"
                  title="Excess"
                  placeholder="Excess On Claim"
                  value={values[rowIndex].excessOnClaimPercentage}
                  error={
                    touched[rowIndex].excessOnClaimPercentage
                      ? errors[rowIndex].excessOnClaimPercentage
                      : ''
                  }
                  onBlur={() =>
                    onFieldBlur('excessOnClaimPercentage', rowIndex)
                  }
                  onChange={(v) =>
                    onFieldUpdate(
                      'excessOnClaimPercentage',
                      v.target.value,
                      rowIndex
                    )
                  }
                  disabled={disableOriginalField}
                  material
                />
              )}
              {values[rowIndex].excessOnClaimType === 'VALUE' && (
                <CurrencyFormField
                  key="excessOnClaimAmount"
                  name="excessOnClaimAmount"
                  title="Excess"
                  placeholder="Excess On Claim"
                  value={values[rowIndex].excessOnClaimAmount || ''}
                  error={
                    touched[rowIndex].excessOnClaimAmount
                      ? errors[rowIndex].excessOnClaimAmount
                      : ''
                  }
                  onBlur={() => onFieldBlur('excessOnClaimAmount', rowIndex)}
                  onChange={(v) =>
                    onFieldUpdate(
                      'excessOnClaimAmount',
                      v.target.value,
                      rowIndex
                    )
                  }
                  disabled={disableOriginalField}
                  material
                  currencySymbol={generalData.currency.Symbol}
                />
              )}
              {values[rowIndex].excessOnClaimType === 'DAYS' && (
                <CurrencyFormField
                  key="excessOnClaimDays"
                  name="excessOnClaimDays"
                  title="Excess (Days)"
                  placeholder="Excess On Claim (Days)"
                  value={values[rowIndex].excessOnClaimDays}
                  error={
                    touched[rowIndex].excessOnClaimDays
                      ? errors[rowIndex].excessOnClaimDays
                      : ''
                  }
                  onBlur={() => onFieldBlur('excessOnClaimDays', rowIndex)}
                  onChange={(v) =>
                    onFieldUpdate('excessOnClaimDays', v.target.value, rowIndex)
                  }
                  disabled={disableOriginalField}
                  material
                />
              )}
              <DatePickerFormField
                key="coverEffectiveFrom"
                name="coverEffectiveFrom"
                title="Effective From"
                placeholder="Effective From"
                value={values[rowIndex].coverEffectiveFrom}
                error={
                  touched[rowIndex].coverEffectiveFrom
                    ? errors[rowIndex].coverEffectiveFrom
                    : ''
                }
                onDateChange={(v) =>
                  onFieldUpdate('coverEffectiveFrom', v, rowIndex)
                }
                disabled
                material
                onBlur={() => onFieldBlur('coverEffectiveFrom', rowIndex)}
              />
              <DatePickerFormField
                key="coverEffectiveTo"
                name="coverEffectiveTo"
                title="Effective To"
                placeholder="Effective To"
                value={values[rowIndex].coverEffectiveTo}
                error={
                  touched[rowIndex].coverEffectiveTo
                    ? errors[rowIndex].coverEffectiveTo
                    : ''
                }
                onDateChange={(v) =>
                  onFieldUpdate('coverEffectiveTo', v, rowIndex)
                }
                onBlur={() => onFieldBlur('coverEffectiveTo', rowIndex)}
                disabled
                material
              />
              {!isNew && !row?.isMain && (
                <SwitchFormField
                  key="isRemoved"
                  name="isRemoved"
                  title="Is Removed"
                  value={values[rowIndex].isRemoved}
                  error={
                    touched[rowIndex].isRemoved
                      ? errors[rowIndex].isRemoved
                      : ''
                  }
                  onBlur={() => onFieldBlur('isRemoved', rowIndex)}
                  onChange={(name, value) => {
                    onFieldUpdate('isRemoved', value, rowIndex, true);
                  }}
                  disabled={disableIsRemoved}
                  className={classes.switchLabelWrapper}
                />
              )}
            </>
          )}
        </div>
        {values[rowIndex].policyCover && !isNew && (
          <>
            <WidgetSection
              title="Amendment Details"
              narrowSpacing
              hideHeaderBorder
              isFieldsGrid
              fieldsPerRow={3}
            >
              <CurrencyFormField
                key="amendmentCoverSumInsured"
                name="amendmentCoverSumInsured"
                title="Sum Insured Variance"
                placeholder="Sum Insured Variance"
                value={values[rowIndex].amendmentCoverSumInsured || ''}
                error={
                  touched[rowIndex].amendmentCoverSumInsured
                    ? errors[rowIndex].amendmentCoverSumInsured
                    : ''
                }
                onBlur={() => onFieldBlur('amendmentCoverSumInsured', rowIndex)}
                onChange={(v) =>
                  onFieldUpdate(
                    'amendmentCoverSumInsured',
                    v.target.value,
                    rowIndex
                  )
                }
                disabled={disableVarianceField}
                material
                allowNegative
                currencySymbol={generalData.currency.Symbol}
                minValue={-values[rowIndex].coverSumInsured}
              />

              {!isGroupPolicy &&
                (values[rowIndex].coverPremiumType === 'VALUE' ? (
                  <CurrencyFormField
                    key="amendmentCoverPremiumValue"
                    name="amendmentCoverPremiumValue"
                    title="Premium Variance"
                    placeholder="Premium Variance"
                    value={values[rowIndex].amendmentCoverPremiumValue || ''}
                    error={
                      touched[rowIndex].amendmentCoverPremiumValue
                        ? errors[rowIndex].amendmentCoverPremiumValue
                        : ''
                    }
                    onBlur={() =>
                      onFieldBlur('amendmentCoverPremiumValue', rowIndex)
                    }
                    onChange={(v) =>
                      onFieldUpdate(
                        'amendmentCoverPremiumValue',
                        v.target.value,
                        rowIndex
                      )
                    }
                    disabled={disableVarianceField}
                    material
                    allowNegative
                    currencySymbol={generalData.currency.Symbol}
                    minValue={-values[rowIndex].coverPremiumValue}
                  />
                ) : (
                  <PercentageFormField
                    key="amendmentCoverPremiumPercentage"
                    name="amendmentCoverPremiumPercentage"
                    title="Premium Percentage Variance"
                    placeholder="Premium Percentage Variance"
                    value={values[rowIndex].amendmentCoverPremiumPercentage}
                    error={
                      touched[rowIndex].amendmentCoverPremiumPercentage
                        ? errors[rowIndex].amendmentCoverPremiumPercentage
                        : ''
                    }
                    onBlur={() =>
                      onFieldBlur('amendmentCoverPremiumPercentage', rowIndex)
                    }
                    onChange={(v) =>
                      onFieldUpdate(
                        'amendmentCoverPremiumPercentage',
                        v.target.value,
                        rowIndex
                      )
                    }
                    disabled={disableVarianceField}
                    material
                    minValue={-values[rowIndex].coverPremiumPercentage}
                  />
                ))}

              {values[rowIndex].excessOnClaimType === 'PERCENTAGE' && (
                <PercentageFormField
                  key="amendmentExcessOnClaimPercentage"
                  name="amendmentExcessOnClaimPercentage"
                  title="Excess Variance"
                  placeholder="Excess On Claim"
                  value={values[rowIndex].amendmentExcessOnClaimPercentage}
                  error={
                    touched[rowIndex].amendmentExcessOnClaimPercentage
                      ? errors[rowIndex].amendmentExcessOnClaimPercentage
                      : ''
                  }
                  onBlur={() =>
                    onFieldBlur('amendmentExcessOnClaimPercentage', rowIndex)
                  }
                  onChange={(v) =>
                    onFieldUpdate(
                      'amendmentExcessOnClaimPercentage',
                      v.target.value,
                      rowIndex
                    )
                  }
                  disabled={disableVarianceField}
                  material
                  disableNegativeValues={false}
                  // minValue={-values[rowIndex].excessOnClaimPercentage}
                  // maxValue={100}
                />
              )}
              {values[rowIndex].excessOnClaimType === 'VALUE' && (
                <CurrencyFormField
                  key="amendmentExcessOnClaimAmount"
                  name="amendmentExcessOnClaimAmount"
                  title="Excess Variance"
                  placeholder="Excess On Claim"
                  value={values[rowIndex].amendmentExcessOnClaimAmount || ''}
                  error={
                    touched[rowIndex].amendmentExcessOnClaimAmount
                      ? errors[rowIndex].amendmentExcessOnClaimAmount
                      : ''
                  }
                  onBlur={() =>
                    onFieldBlur('amendmentExcessOnClaimAmount', rowIndex)
                  }
                  onChange={(v) =>
                    onFieldUpdate(
                      'amendmentExcessOnClaimAmount',
                      v.target.value,
                      rowIndex
                    )
                  }
                  disabled={disableVarianceField}
                  material
                  allowNegative
                  currencySymbol={generalData.currency.Symbol}
                  minValue={-values[rowIndex].excessOnClaimAmount}
                />
              )}
              {values[rowIndex].excessOnClaimType === 'DAYS' && (
                <CurrencyFormField
                  key="amendmentExcessOnClaimDays"
                  name="amendmentExcessOnClaimDays"
                  title="Excess Variance (Days)"
                  placeholder="Excess On Claim (Days)"
                  value={values[rowIndex].amendmentExcessOnClaimDays}
                  error={
                    touched[rowIndex].amendmentExcessOnClaimDays
                      ? errors[rowIndex].amendmentExcessOnClaimDays
                      : ''
                  }
                  onBlur={() =>
                    onFieldBlur('amendmentExcessOnClaimDays', rowIndex)
                  }
                  onChange={(v) =>
                    onFieldUpdate(
                      'amendmentExcessOnClaimDays',
                      v.target.value,
                      rowIndex
                    )
                  }
                  disabled={disableVarianceField}
                  allowNegative
                  material
                  minValue={-values[rowIndex].excessOnClaimDays}
                />
              )}
            </WidgetSection>
          </>
        )}
      </div>
    );
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Policy Covers">
        {values?.map((v: IAmendmentCover, index: number) =>
          renderRow(v, index)
        )}
        {values?.length < Object.keys(allCoverslov)?.length &&
          !isDisabled &&
          generalData.loggedInUser.allowPlanCoverEdits && (
            <button className={classes.addBtn} onClick={onRowAdd}>
              <AddCircleOutlineIcon fontSize="small" color="error" />
            </button>
          )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default PolicyCovers;
